import { ReactNode } from "react";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Modal, { ModalWidth } from "@components/modal/modal";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

type StatusTone = "enabled" | "neutral" | "warning";

const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  enabled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-gray-100 text-gray-700 border-gray-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
};

const IntegrationDetailLayout = ({
  open,
  onClose,
  name,
  logo,
  logoNode,
  description,
  statusLabel,
  statusTone = "neutral",
  actions,
  banner,
  tabs,
  width = ModalWidth.normal,
  children,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  logo: string;
  logoNode?: ReactNode;
  description?: ReactNode;
  statusLabel?: string;
  statusTone?: StatusTone;
  actions?: ReactNode;
  banner?: ReactNode;
  tabs?: ReactNode;
  width?: ModalWidth;
  children?: ReactNode;
}) => (
  <Modal open={open} onClose={onClose} width={width} alignedTop>
    <div className="flex flex-col max-h-[calc(100vh-8rem)]">
      <div className="flex items-start justify-between gap-6 px-6 pt-6 pb-4">
        <div className="flex items-start gap-6 flex-1 min-w-0">
          {logoNode ? (
            <div className="border rounded-lg p-2 bg-white shrink-0 h-[3.5rem] w-[3.5rem] flex items-center justify-center">
              {logoNode}
            </div>
          ) : (
            logo && (
              <div className="border rounded-lg p-2 bg-white shrink-0">
                <img
                  src={getAssetUrl(logo)}
                  alt=""
                  className="h-10 w-10 object-contain"
                />
              </div>
            )
          )}
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-gray-900">{name}</h2>
              {statusLabel && (
                <span
                  className={classNames(
                    "rounded-full border px-2.5 py-0.5 text-xs font-medium",
                    STATUS_TONE_CLASS[statusTone],
                  )}
                >
                  {statusLabel}
                </span>
              )}
            </div>
            {description && (
              <div className="text-sm text-gray-500">{description}</div>
            )}
          </div>
        </div>
        <Button
          icon={getTopicflowIcon("close")}
          onClick={onClose}
          theme={buttonTheme.iconGray}
          aria-label="Close integration dialog"
        />
      </div>

      {tabs && <div className="px-6 shrink-0">{tabs}</div>}

      {(banner || children) && (
        <div className="overflow-y-auto px-6 py-5 space-y-6">
          {banner}
          {children}
        </div>
      )}

      {actions && (
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          {actions}
        </div>
      )}
    </div>
  </Modal>
);

export default IntegrationDetailLayout;
